import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../../components/shared/toast';
import Breadcrumbs from './components/Breadcrumbs';
import BasicDetailsTab from './components/BasicDetailsTab';
import SongsTab from './components/SongsTab';
import ArtistsTab from './components/ArtistsTab';
import DocumentsTab from './components/DocumentsTab';
import CasesNotesTab from './components/CasesNotesTab';
import AlbumForm from '../Albums/components/AlbumForm';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import { apiClient } from '../../api/apiClient';
import { GET_ALBUM_DETAILS_URL, UPDATE_ALBUM_URL, DELETE_ALBUM_URL } from '../../api/album';
import en from '../../translations/en.json';
import './AlbumDetails.css';

function AlbumDetails({ album: initialAlbum, onBack, onSelectSong, onSelectArtist, onEdit, onDelete, onSelectCase }) {
  const [activeTab, setActiveTab] = useState('Basic Details');
  const [albumData, setAlbumData] = useState(initialAlbum || null);
  const [isEditing, setIsEditing] = useState(false);

  // Delete modal state
  const [showPrimaryDeleteModal, setShowPrimaryDeleteModal] = useState(false);
  const [showSecondaryDeleteModal, setShowSecondaryDeleteModal] = useState(false);
  const [dependentSongs, setDependentSongs] = useState([]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const fetchDetails = useCallback(async () => {
    if (!initialAlbum || !initialAlbum.id) return;
    try {
      const res = await apiClient(GET_ALBUM_DETAILS_URL(initialAlbum.id));
      if (res) {
        setAlbumData(res);
      }
    } catch (err) {
      console.error('Failed to fetch album details:', err);
    }
  }, [initialAlbum]);

  useEffect(() => {
    fetchDetails();
  }, [initialAlbum?.id]);

  const currentAlbum = albumData || initialAlbum || {};

  // Edit handler
  const handleSaveForm = async (formData) => {
    try {
      const response = await apiClient(UPDATE_ALBUM_URL(currentAlbum.id), {
        method: 'PUT',
        body: formData
      });
      if (response) {
        toast.success('Album updated successfully');
        setIsEditing(false);
        await fetchDetails();
        if (onEdit) onEdit(currentAlbum);
      }
    } catch (err) {
      console.error('Error updating album:', err);
      toast.warning(err.message || 'Failed to update album.');
    }
  };

  // Delete handlers
  const handleDeleteClick = () => {
    setShowPrimaryDeleteModal(true);
  };

  const handleConfirmPrimaryDelete = async () => {
    setShowPrimaryDeleteModal(false);
    try {
      const response = await apiClient(DELETE_ALBUM_URL(currentAlbum.id), {
        method: 'DELETE'
      });

      if (response && response.hasDependencies) {
        setDependentSongs(response.dependentSongs || []);
        setShowSecondaryDeleteModal(true);
      } else if (response && response.success !== false) {
        toast.success('Album deleted successfully');
        if (onDelete) onDelete(currentAlbum);
        if (onBack) onBack();
      }
    } catch (err) {
      console.error('Error deleting album:', err);
      toast.error(err.message || 'Failed to delete album.');
    }
  };

  const handleConfirmSecondaryDelete = async () => {
    setShowSecondaryDeleteModal(false);
    try {
      const response = await apiClient(`${DELETE_ALBUM_URL(currentAlbum.id)}?force=true`, {
        method: 'DELETE'
      });

      if (response && response.success !== false) {
        toast.success('Album deleted successfully');
        if (onDelete) onDelete(currentAlbum);
        if (onBack) onBack();
      }
    } catch (err) {
      console.error('Error force deleting album:', err);
      toast.error(err.message || 'Failed to delete album.');
    }
  };

  if (isEditing) {
    return (
      <AlbumForm 
        existingAlbum={currentAlbum}
        onSave={handleSaveForm}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  const tabs = [
    { id: 'Basic Details', label: t('albumDetails.tabBasicDetails') },
    { id: 'Songs', label: t('albumDetails.tabSongs') },
    { id: 'Artists', label: t('albumDetails.tabArtists') },
    { id: 'Documents', label: t('albumDetails.tabDocuments') },
    { id: 'Cases and Notes', label: t('albumDetails.tabCasesNotes') }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Basic Details':
        return <BasicDetailsTab album={currentAlbum} />;
      case 'Songs':
        return <SongsTab album={currentAlbum} onSelectSong={onSelectSong} />;
      case 'Artists':
        return <ArtistsTab album={currentAlbum} onSelectArtist={onSelectArtist} />;
      case 'Documents':
        return <DocumentsTab album={currentAlbum} />;
      case 'Cases and Notes':
        return <CasesNotesTab album={currentAlbum} onSelectCase={onSelectCase} />;
      default:
        return null;
    }
  };

  const albumName = currentAlbum.name || 'Album Name';
  const albumArtists = currentAlbum.recordLabelName || currentAlbum.tag || 'Mathakayan Obe';
  const coverUrl = currentAlbum.imageUrl || currentAlbum.image_url || 'https://images.unsplash.com/photo-1557200134-90327ee9fafa?auto=format&fit=crop&q=80&w=300&h=300';

  return (
    <div className="album-details-page">
      {/* Breadcrumbs Section */}
      <Breadcrumbs albumName={albumName} onBack={onBack} />

      {/* Main Header / Banner Section */}
      <div className="album-details-banner">
        <div className="banner-left-content">
          <div className="album-status-indicator">
            <span className="indicator-dot"></span>
            <span className="indicator-text">{t('albumDetails.statusActive')}</span>
          </div>
          <h1 className="album-main-title">{albumName}</h1>
          <p className="album-main-subtitle">{albumArtists}</p>
        </div>

        {/* Cover Art card with change option */}
        <div className="banner-artwork-card" style={{ backgroundImage: `url(${coverUrl})` }}>
          <div className="artwork-overlay">
            <button 
              type="button" 
              className="change-artwork-btn"
              onClick={() => toast.info('Change artwork clicked...')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              <span>{t('albumDetails.changeArtwork')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Selectors Navigation */}
      <div className="album-details-tabs-row">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`details-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab panel body wrapper */}
      <div className="album-details-content-card">
        <div className="tab-panel-body">
          {renderTabContent()}
        </div>

        {/* Action Button Footer section - shown on Basic Details tab */}
        {activeTab === 'Basic Details' && (
          <div className="album-details-footer">
            <button 
              type="button" 
              className="footer-edit-btn"
              onClick={() => setIsEditing(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              <span>{t('albumDetails.editAlbum')}</span>
            </button>

            <button 
              type="button" 
              className="footer-deactivate-btn"
              onClick={handleDeleteClick}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              <span>Delete Album</span>
            </button>
          </div>
        )}
      </div>

      {/* Primary Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPrimaryDeleteModal}
        type="danger"
        title="Delete Album"
        message={`Are you sure you want to permanently delete the album "${currentAlbum.name || 'Album'}"?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmPrimaryDelete}
        onCancel={() => setShowPrimaryDeleteModal(false)}
      />

      {/* Secondary Delete Confirmation Modal (Active Songs Dependency Warning) */}
      {showSecondaryDeleteModal && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-card-container" style={{ width: '500px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => {
                setShowSecondaryDeleteModal(false);
                setDependentSongs([]);
              }}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div className="modal-icon-container danger" style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading" style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>
                  Delete Album Warning
                </h3>
                <p className="modal-description-text" style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px' }}>
                  This album is currently assigned to the following active songs. Continuing will remove these album relationships as well.
                </p>
              </div>
            </div>

            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '20px', background: '#f8fafc' }}>
              {dependentSongs.map((song) => (
                <div key={song.id} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{song.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Artist: {song.artist}</div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', marginBottom: '20px' }}>
              Deleting this album will also remove its relationship from these songs.
            </p>

            <div className="modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => {
                  setShowSecondaryDeleteModal(false);
                  setDependentSongs([]);
                }}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm danger"
                onClick={handleConfirmSecondaryDelete}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: '600' }}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlbumDetails;
